import { Controller } from '@hotwired/stimulus'
import { getCount } from '../api/common/count'
import { buildStaticUrl, prepareStaticUrlFormData } from '../../common/static_url_builder'

export default class extends Controller {
  static targets = [
    'modal',
    'tab',
    'panel',
    'form',
    'submit',
    'notifyText',
    'featureCheckbox',
    'stationCheckbox',
    'prefectureId',
    'cityId',
    'ordinanceDesignatedCityId',
    'stationId',
    'stationList',
    'cityList',
    'ordinanceDesignatedCityList',
    'citySelectedContent',
    'submitButton',
    'areaPrefectureView',
    'areaRegionView',
    'prefectureTabs',
    'cityCheckboxContainer',
    'checkboxAll',
    'checkboxes',
  ]

  connect () {
    this.prefectureCityCache = {}
    this.setFacilitiesCount()
    this.initializeAreaPanel()
    window.addEventListener('open-condition-search-modal', this._toggleModalHandler = (e) => {
      this.openModal(e.detail?.modalIndex)
    })
  }

  disconnect () {
    window.removeEventListener('open-condition-search-modal', this._toggleModalHandler)
  }

  get className () {
    return this.element.dataset.classname
  }

  toggleModal (arg) {
    if (typeof arg === 'object' && arg && 'preventDefault' in arg) {
      arg.preventDefault()
      this.openModalByIndex(arg.target.dataset.modalIndex)
    } else {
      this.openModalByIndex(arg)
    }
  }

  // `open-condition-search-modal` イベント専用の「開く専用」ハンドラ。
  // サイドバーの「エリア」ボタンは常にモーダルを開く動作にする（トグルしない）。
  // すでに開いている場合は全パネル・タブをリセットしてから対象を表示（異なるindexで
  // 呼ばれた場合のパネル二重表示を防ぐ）。
  openModal (targetModalIndex) {
    if (this.modalTarget.classList.contains(`${this.className}::modal-open`)) {
      if (this.hasPanelTarget) {
        this.panelTargets.forEach(p => p.classList.add(`${this.classNameModal()}::hidden`))
      }
      if (this.hasTabTarget) {
        this.tabTargets.forEach(t => t.classList.remove(`${this.classNameModal()}::active`))
      }
      this.modalInitialize(targetModalIndex)
      return
    }
    this.modalTarget.classList.add(`${this.className}::modal-open`)
    this.modalInitialize(targetModalIndex)
    this.ensureCTAWithRetry()
  }

  openModalByIndex (targetModalIndex) {
    if (this.modalTarget.classList.contains(`${this.className}::modal-open`)) {
      this.modalTarget.classList.remove(`${this.className}::modal-open`)
    } else {
      this.modalTarget.classList.add(`${this.className}::modal-open`)
      this.modalInitialize(targetModalIndex)
      this.ensureCTAWithRetry()
    }
  }

  modalInitialize (targetModalIndex) {
    if (this.hasTabTarget) {
      this.tabTargets[targetModalIndex].classList.add('viewcomponent::site::parts::retry_search::modal::desktop::active')
    }
    if (this.hasPanelTarget) {
      this.panelTargets[targetModalIndex].classList.remove('viewcomponent::site::parts::retry_search::modal::desktop::hidden')
    }
  }

  ga () {
    window.gtagEvent('event', '', 'search_tap_modal')
  }

  validate (e) {
    e.preventDefault()

    // 静的URLに直接遷移（#12811 方式B）。変換不可能な場合は従来通り /search/ にフォールバック
    // ※ prefecture_id 削除より前に実行することで、同一都道府県内の市区町村選択時も静的URLを生成できる
    try {
      const formData = new FormData(this.formTarget)
      prepareStaticUrlFormData(formData, this.cityListTargets, this.ordinanceDesignatedCityListTargets, this.cityIdTargets, this.ordinanceDesignatedCityIdTargets)
      const staticUrl = buildStaticUrl(formData)
      if (staticUrl) {
        window.location.href = staticUrl
        return
      }
    } catch (error) {
      console.error('静的URL生成に失敗。フォーム送信にフォールバック:', error)
    }

    // 静的URLに変換できない場合のみ従来の /search/ にフォールバック
    // city_listとordinance_designated_city_list、station_listのいずれかがチェックされている場合
    if (this.cityListTargets.some((element) => element.checked) ||
      this.ordinanceDesignatedCityListTargets.some((element) => element.checked) ||
      this.stationListTargets.some((element) => element.checked)
    ) {
      // prefecture_idが存在したら削除し、該当のhidden_fieldを削除する
      if (this.prefectureIdTargets.length > 0) {
        // targetsの配列をコピーしてからremoveを実行することで、
        // イテレーション中の配列変更による問題を回避
        Array.from(this.prefectureIdTargets).forEach((element) => element.remove())
      }
    }

    this.formTarget.submit()
  }

  clickAreaCheckBox (e) {
    // 駅の場合は別路線の同一駅チェックも変更する必要がある
    if (e.target.name === 'station_ids[]') {
      Array.from(this.stationListTargets).filter(stationList => stationList.value === e.target.value).forEach(stationList => { return (stationList.checked = e.target.checked) })
    }

    // 路線・エリアの場合は選択中のものを下部に表示する
    if (['station_ids[]', 'city_ids[]', 'ordinance_designated_city_ids[]'].includes(e.target.name)) {
      this.setSelectedContentList(e)
    }

    // 政令指定都市グループの親子チェックボックス連動
    if (this.hasCityCheckboxContainerTarget) {
      if (e.target.name === 'ordinance_designated_city_ids[]' || e.target.dataset.groupType === 'prefecture-all') {
        this.syncChildCheckboxes(e.target)
      } else if (e.target.name === 'city_ids[]') {
        this.syncParentCheckbox(e.target)
      }
    }
  }

  changeFormValue (e) {
    this.setFacilitiesCount()
  }

  manipulateString (a, b) {
    // 文字列aに文字列bが含まれているかチェック
    if (a.includes(b)) {
      // 文字列bが含まれている場合は、文字列aから文字列bを削除して返す
      return a.replace(b, '')
    } else {
      // 文字列bが含まれていない場合は、文字列aに文字列bを追加して返す
      return a + ` ${b}`
    }
  }

  setSelectedContentList (e) {
    const selectedContent = e.target.closest('label').querySelector('span').textContent
    const replacementText = this.manipulateString(this.citySelectedContentTarget.textContent, selectedContent)
    this.citySelectedContentTarget.innerHTML = replacementText
  }

  setFacilitiesCount () {
    if (typeof FormData !== 'function') {
      return
    }

    const formData = new FormData(this.formTarget)
    // city_ids, ordinance_designated_city_ids, station_idsのいずれかがチェックされている場合、prefecture_idは検索条件に含めない
    const prefectureId = formData.getAll('city_ids[]').length > 0 || formData.getAll('ordinance_designated_city_ids[]').length > 0 || formData.getAll('station_ids[]').length > 0 ? null : formData.get('prefecture_id')

    const params = {
      prefecture_id: prefectureId,
      city_ids: formData.getAll('city_ids[]'),
      ordinance_designated_city_ids: formData.getAll('ordinance_designated_city_ids[]'),
      station_ids: formData.getAll('station_ids[]'),
      admission_fee_min: formData.get('admission_fee_min'),
      admission_fee_max: formData.get('admission_fee_max'),
      monthly_fee_min: formData.get('monthly_fee_min'),
      monthly_fee_max: formData.get('monthly_fee_max'),
      walking_time: formData.get('walking_time'),
      care_level_ids: formData.getAll('care_level_ids[]'),
      feature_content_ids: formData.getAll('feature_content_ids[]'),
      category_ids: formData.getAll('category_ids[]'),
      medical_care_ids: formData.getAll('medical_care_ids[]'),
      with_review: formData.get('with_review'),
      vacancies_exist: formData.get('vacancies_exist'),
      welfare_acceptable: formData.get('welfare_acceptable'),
      dementia_acceptable: formData.get('dementia_acceptable'),
      no_joint_surety_required: formData.get('no_joint_surety_required'),
    }

    getCount(params).then((count) => {
      const countElement = this.submitTargets[0]
      const startNumber = parseInt(countElement.textContent)
      const endNumber = count.result

      this.submitTargets.forEach((element) => {
        if (count.result >= 10000) {
          element.innerText = '10000+'
          this.notifyFacilitiesCountIsOver()
          this.submitButtonEnabled()
        } else if (count.result <= 0) {
          this.submitButtonDisabled()
          element.innerText = count.result
          window.gtagEvent('event', '', '0_facilities_modal')
        } else {
          this.submitButtonEnabled()
          element.innerText = count.result
          this.animateCount(countElement, startNumber, endNumber)
        }
      })
    }).catch((error) => {
      // APIエラー時は検索ボタンを有効化してユーザー操作を継続可能にする
      console.error('Failed to fetch facilities count:', error)
      this.submitButtonEnabled()
    })
  }

  submitButtonEnabled () {
    this.submitButtonTarget.disabled = false
    this.submitButtonTarget.classList.remove('viewcomponent::site::parts::retry_search::modal::desktop::disabled')
  }

  submitButtonDisabled () {
    this.submitButtonTarget.disabled = true
    this.submitButtonTarget.classList.add('viewcomponent::site::parts::retry_search::modal::desktop::disabled')
  }

  notifyFacilitiesCountIsOver () {
    if (this.hasNotifyTextTarget) {
      this.notifyTextTarget.style.display = 'block'
    }
  }

  animateCount (element, start, end) {
    let startTime = null

    const updateNumber = (timestamp) => {
      if (!startTime) startTime = timestamp
      const progress = Math.min((timestamp - startTime) / 200, 1)
      const currentNumber = Math.floor(start + (end - start) * progress)
      element.textContent = currentNumber

      if (progress < 1) {
        requestAnimationFrame(updateNumber)
      }
    }

    requestAnimationFrame(updateNumber)
  }

  uncheckAssociatedFeatureCheckboxes (e) {
    if (!e.target.checked) {
      const uncheckFeatureId = e.target.getAttribute('data-feature-content-id')
      this.featureCheckboxTargets.forEach((checkbox) => {
        if (checkbox.checked && checkbox.getAttribute('data-feature-content-id') === uncheckFeatureId) {
          checkbox.checked = false
          const changeEvent = new Event('change')
          checkbox.dispatchEvent(changeEvent)
        }
      })
    }
  }

  classNameModal () {
    return 'viewcomponent::site::parts::retry_search::modal::desktop'
  }

  modalDesktopComponentTarget () {
    return 'data-site--parts--retry-search--modal--desktop--component-target'
  }

  desktopComponentTarget () {
    return 'data-site--parts--retry-search--desktop--component-target'
  }

  initializeAreaPanel () {
    if (!this.hasAreaPrefectureViewTarget) return

    // panel要素からデータ属性を読み込む
    const panelEl = this.panelTargets[0]
    if (!panelEl) return

    const initialPrefectureId = panelEl.dataset.initialPrefectureId
    const initialSelectedPrefectureIdsJson = panelEl.dataset.initialSelectedPrefectureIdsJson
    const regionPrefecturesJson = panelEl.dataset.regionPrefecturesJson
    const allRegionsJson = panelEl.dataset.allRegionsJson

    if (!regionPrefecturesJson || !allRegionsJson) return

    try {
      this.regionPrefectures = JSON.parse(regionPrefecturesJson)
      this.allRegions = JSON.parse(allRegionsJson)
    } catch (error) {
      console.error('エリアパネルの初期化に失敗しました:', error)
      return
    }

    // 選択済み都道府県IDの初期セット（ページ再読み込み後のアクティブスタイル復元に使用）
    this.initialSelectedPrefectureIds = new Set(
      initialSelectedPrefectureIdsJson ? JSON.parse(initialSelectedPrefectureIdsJson) : [],
    )
    this.visitedPrefectureIds = new Set()

    // 初期都道府県IDから地方を特定して都道府県タブを構築
    const prefId = initialPrefectureId ? Number(initialPrefectureId) : null

    if (prefId) {
      const regionId = this.findRegionIdByPrefectureId(prefId)
      this.buildPrefectureTabs(regionId, prefId)
      this.buildRegionView()
      this.showPrefectureView(prefId)
    } else {
      // 都道府県未選択時（全国検索など）は地方エリア選択画面を初期表示
      this.buildRegionView()
      this.showRegionView()
    }
  }

  findRegionIdByPrefectureId (prefectureId) {
    if (!prefectureId || !this.regionPrefectures) return 1
    for (const [regionId, prefs] of Object.entries(this.regionPrefectures)) {
      if (prefs.some(p => p.id === prefectureId)) return Number(regionId)
    }
    return 1
  }

  showRegionView (e) {
    if (e) e.preventDefault()
    // 地方ビューに切り替える前に現在の選択状態を保存（地方をまたいだ選択保持に必要）
    this.preserveCheckedSelections()
    if (this.hasAreaPrefectureViewTarget) {
      this.areaPrefectureViewTarget.style.display = 'none'
    }
    if (this.hasAreaRegionViewTarget) {
      this.areaRegionViewTarget.style.display = 'block'
    }
    // 選択済み都道府県のアクティブスタイルを反映するため再描画
    this.buildRegionView()
  }

  showPrefectureView (prefectureId) {
    if (this.hasAreaRegionViewTarget) {
      this.areaRegionViewTarget.style.display = 'none'
    }
    if (this.hasAreaPrefectureViewTarget) {
      this.areaPrefectureViewTarget.style.display = 'block'
    }
    this.switchPrefectureTab(prefectureId)
    this.loadCitiesForTab(prefectureId)
  }

  switchPrefectureTab (prefectureId) {
    if (!this.hasPrefectureTabsTarget) return
    const selectedPrefIds = this.getSelectedPrefectureIds()
    const tabs = this.prefectureTabsTarget.querySelectorAll('li')
    tabs.forEach(tab => {
      const tabPrefId = Number(tab.dataset.prefectureId)
      if (tabPrefId === Number(prefectureId) || selectedPrefIds.has(tabPrefId)) {
        tab.classList.add(`${this.classNameModal()}::active`)
      } else {
        tab.classList.remove(`${this.classNameModal()}::active`)
      }
    })
    this.currentPrefectureId = Number(prefectureId)
  }

  buildPrefectureTabs (regionId, activePrefectureId) {
    if (!this.hasPrefectureTabsTarget) return
    const prefs = this.regionPrefectures?.[regionId] ?? []
    const selectedPrefIds = this.getSelectedPrefectureIds()
    const html = prefs.map(pref => {
      const isActive = pref.id === activePrefectureId || selectedPrefIds.has(pref.id)
      const activeClass = isActive ? ` ${this.classNameModal()}::active` : ''
      return `<li class="${this.classNameModal()}::prefecture-tab${activeClass}"
        data-prefecture-id="${pref.id}"
        data-action="click->site--parts--retry-search--desktop--component#onPrefectureTabClick"
      >${pref.name}</li>`
    }).join('')
    this.prefectureTabsTarget.innerHTML = html
  }

  buildRegionView () {
    if (!this.hasAreaRegionViewTarget || !this.allRegions) return
    const selectedCityIds = this.cityIdTargets.map(i => Number(i.value))
    const selectedOrdinanceIds = this.ordinanceDesignatedCityIdTargets.map(i => Number(i.value))
    // hidden inputs の data-value-pref から選択済み都道府県IDを取得（キャッシュ未取得の都道府県も判定可能）
    const selectedPrefIds = this.getSelectedPrefectureIds()

    const html = this.allRegions.map(region => {
      const prefs = this.regionPrefectures?.[region.id] ?? []
      const chips = prefs.map(pref => {
        const cache = this.prefectureCityCache[pref.id]
        const hasSelection = selectedPrefIds.has(pref.id) || (cache
          ? cache.cities.some(c => selectedCityIds.includes(c.id)) ||
            // eslint-disable-next-line camelcase
            cache.ordinance_designated_cities.some(o => selectedOrdinanceIds.includes(o.id))
          : false)
        const isActive = hasSelection ? ` ${this.classNameModal()}::active` : ''
        return `<li class="${this.classNameModal()}::prefecture-chip${isActive}"
          data-prefecture-id="${pref.id}"
          data-action="click->site--parts--retry-search--desktop--component#onRegionPrefectureClick"
        >${pref.name}</li>`
      }).join('')
      return `<div class="${this.classNameModal()}::region-block">
        <div class="${this.classNameModal()}::region-name">${region.name}</div>
        <ul class="${this.classNameModal()}::prefecture-chips">${chips}</ul>
      </div>`
    }).join('')
    this.areaRegionViewTarget.innerHTML = html
  }

  onPrefectureTabClick (e) {
    const prefectureId = Number(e.currentTarget.dataset.prefectureId)
    this.preserveCheckedSelections()
    this.switchPrefectureTab(prefectureId)
    this.loadCitiesForTab(prefectureId)
  }

  onRegionPrefectureClick (e) {
    const prefectureId = Number(e.currentTarget.dataset.prefectureId)
    const regionId = this.findRegionIdByPrefectureId(prefectureId)
    this.buildPrefectureTabs(regionId, prefectureId)
    this.showPrefectureView(prefectureId)
  }

  // hidden inputs の data-value-pref から選択済み都道府県IDのSetを返す
  // （preserveCheckedSelections() が保存した都道府県IDを取得する）
  // 未訪問の都道府県については初期サーバーデータ（initialSelectedPrefectureIds）をフォールバックとして使用
  getSelectedPrefectureIds () {
    const ids = new Set()
    this.cityIdTargets.forEach(i => {
      if (i.dataset.valuePref) ids.add(Number(i.dataset.valuePref))
    })
    this.ordinanceDesignatedCityIdTargets.forEach(i => {
      if (i.dataset.valuePref) ids.add(Number(i.dataset.valuePref))
    })
    // 未訪問の都道府県はサーバー側の初期選択状態をフォールバックとして使用
    // （訪問済み = preserveCheckedSelections() が呼ばれた都道府県は data-value-pref を信頼する）
    if (this.initialSelectedPrefectureIds) {
      this.initialSelectedPrefectureIds.forEach(prefId => {
        if (!this.visitedPrefectureIds || !this.visitedPrefectureIds.has(prefId)) {
          ids.add(prefId)
        }
      })
    }
    return ids
  }

  // 都道府県タブを切り替える前に、現在表示中のタブでチェックされている市区町村を
  // hidden input として保存する。タブ切り替えで innerHTML が上書きされてもフォーム送信時に
  // 選択状態が維持され、prepareStaticUrlFormData() でクロス都道府県判定にも使われる。
  preserveCheckedSelections () {
    const prefId = this.currentPrefectureId
    if (!prefId) return
    // 訪問済みとして記録（以降はサーバー初期値より data-value-pref を優先する）
    this.visitedPrefectureIds = this.visitedPrefectureIds || new Set()
    this.visitedPrefectureIds.add(prefId)
    this.cityListTargets.forEach(el => {
      if (!el.checked) return
      if (this.cityIdTargets.some(h => Number(h.value) === Number(el.value))) return
      const hidden = document.createElement('input')
      hidden.type = 'hidden'
      hidden.name = 'city_ids[]'
      hidden.value = el.value
      hidden.dataset.valuePref = String(prefId)
      hidden.setAttribute(`data-${this.identifier}-target`, 'cityId')
      this.formTarget.appendChild(hidden)
    })
    this.ordinanceDesignatedCityListTargets.forEach(el => {
      if (!el.checked) return
      if (this.ordinanceDesignatedCityIdTargets.some(h => Number(h.value) === Number(el.value))) return
      const hidden = document.createElement('input')
      hidden.type = 'hidden'
      hidden.name = 'ordinance_designated_city_ids[]'
      hidden.value = el.value
      hidden.dataset.valuePref = String(prefId)
      hidden.setAttribute(`data-${this.identifier}-target`, 'ordinanceDesignatedCityId')
      this.formTarget.appendChild(hidden)
    })
  }

  loadCitiesForTab (prefectureId) {
    if (!this.hasCityCheckboxContainerTarget) return

    // キャッシュにあれば再描画
    if (this.prefectureCityCache[prefectureId]) {
      const cached = this.prefectureCityCache[prefectureId]
      // eslint-disable-next-line camelcase
      this.cityCheckboxContainerTarget.innerHTML = this.createGroupedCityBlock(cached.ordinance_designated_cities, cached.cities)
      // prepareStaticUrlFormData がクロス都道府県選択を判定するために参照する
      this.cityCheckboxContainerTarget.dataset.valuePref = prefectureId
      return
    }

    this.cityCheckboxContainerTarget.innerHTML = `<li class="${this.classNameModal()}::loading"><div class="${this.classNameModal()}::loader"></div></li>`

    const params = new URLSearchParams()
    params.append('prefecture_ids[]', prefectureId)
    const path = '/api/areas/cities/?' + params.toString()

    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null
    const timeoutId = controller ? setTimeout(() => controller.abort(), 15000) : null

    fetch(path, controller ? { signal: controller.signal } : {})
      .then(data => {
        clearTimeout(timeoutId)
        return data.json()
      })
      .then(res => {
        const prefecture = res.prefectures?.[0]
        if (!prefecture) return
        // eslint-disable-next-line camelcase
        const { cities, ordinance_designated_cities } = prefecture
        // eslint-disable-next-line camelcase
        this.prefectureCityCache[prefectureId] = { cities, ordinance_designated_cities }
        if (this.currentPrefectureId === Number(prefectureId)) {
          // eslint-disable-next-line camelcase
          this.cityCheckboxContainerTarget.innerHTML = this.createGroupedCityBlock(ordinance_designated_cities, cities)
          // prepareStaticUrlFormData がクロス都道府県選択を判定するために参照する
          this.cityCheckboxContainerTarget.dataset.valuePref = prefectureId
        }
      })
      .catch((error) => {
        clearTimeout(timeoutId)
        console.error('市区町村の取得に失敗しました:', error)
        if (this.hasCityCheckboxContainerTarget && this.currentPrefectureId === Number(prefectureId)) {
          this.cityCheckboxContainerTarget.innerHTML = ''
        }
      })
  }

  createGroupedCityBlock (ordinanceDesignatedCities, cities) {
    // ordinance_designated_city_id による確実なグループ分け
    const selectedOrdinanceIds = this.ordinanceDesignatedCityIdTargets.map(i => Number(i.value))
    const selectedCityIds = this.cityIdTargets.map(i => Number(i.value))

    const ordinanceCityList = Array.isArray(ordinanceDesignatedCities)
      ? [...ordinanceDesignatedCities].sort((a, b) => a.id - b.id)
      : []

    const wardsGrouped = ordinanceCityList.reduce((acc, odc) => { acc[odc.id] = []; return acc }, {})
    const ungroupedCities = []

    if (Array.isArray(cities)) {
      cities.forEach(city => {
        if (city.ordinance_designated_city_id && wardsGrouped[city.ordinance_designated_city_id] !== undefined) {
          wardsGrouped[city.ordinance_designated_city_id].push(city)
        } else {
          ungroupedCities.push(city)
        }
      })
    }

    const html = []

    ordinanceCityList.forEach(odc => {
      const isChecked = this.shouldCheck(selectedOrdinanceIds, odc.id)
      this.removeInputHidden(this.ordinanceDesignatedCityIdTargets, odc.id)

      html.push(this.renderGroupHeader({
        id: odc.id,
        name: `${odc.name}すべて`,
        checked: isChecked,
        inputName: 'ordinance_designated_city_ids[]',
        targetName: 'ordinanceDesignatedCityList',
        facilityCount: odc.facility_count,
      }))

      wardsGrouped[odc.id].forEach(ward => {
        const isWardChecked = this.shouldCheck(selectedCityIds, ward.id)
        this.removeInputHidden(this.cityIdTargets, ward.id)
        html.push(this.renderCityCheckboxLi({
          id: ward.id,
          name: ward.name,
          checked: isWardChecked,
          inputName: 'city_ids[]',
          targetName: 'cityList',
          facilityCount: ward.facility_count,
        }))
      })
    })

    // 政令指定都市あり都道府県でその他市区町村がある場合はグループ見出しを表示
    if (ordinanceCityList.length > 0 && ungroupedCities.length > 0) {
      html.push(this.renderOtherCitiesHeader())
    }

    // 政令指定都市なし都道府県では「{都道府県名}内すべて」見出しを表示
    if (ordinanceCityList.length === 0 && ungroupedCities.length > 0) {
      const prefectureName = this.getPrefectureNameById(this.currentPrefectureId)
      if (prefectureName) {
        const totalCount = ungroupedCities.reduce((sum, c) => sum + (c.facility_count || 0), 0)
        const allChecked = ungroupedCities.every(c => selectedCityIds.includes(c.id))
        html.push(this.renderPrefectureAllHeader(prefectureName, totalCount, allChecked))
      }
    }

    ungroupedCities.forEach(city => {
      const isCityChecked = this.shouldCheck(selectedCityIds, city.id)
      this.removeInputHidden(this.cityIdTargets, city.id)
      html.push(this.renderCityCheckboxLi({
        id: city.id,
        name: city.name,
        checked: isCityChecked,
        inputName: 'city_ids[]',
        targetName: 'cityList',
        facilityCount: city.facility_count,
      }))
    })

    return html.join('')
  }

  renderGroupHeader ({ id, name, checked, inputName, targetName, facilityCount }) {
    const countText = facilityCount != null ? `<span class="${this.classNameModal()}::facility-count">(${this.formatCount(facilityCount)}件)</span>` : ''
    const zeroCountClass = facilityCount === 0 ? ' is-zero-count' : ''
    return `
      <li class="${this.classNameModal()}::city-group-header${zeroCountClass}">
        <label class="${this.classNameModal()}::check">
          <input class="check-input" type="checkbox" value="${id}" ${checked ? "checked='checked'" : ''} name="${inputName}"
            data-action="change->site--parts--retry-search--desktop--component#changeFormValue click->site--parts--retry-search--desktop--component#clickAreaCheckBox"
            data-site--parts--retry-search--desktop--component-target="${targetName}">
          <i></i>
          <span class="${this.classNameModal()}::checkbox-text">${name}${countText}</span>
        </label>
      </li>`
  }

  renderPrefectureAllHeader (prefectureName, facilityCount, checked = false) {
    const countText = facilityCount != null ? `<span class="${this.classNameModal()}::facility-count">(${this.formatCount(facilityCount)}件)</span>` : ''
    const zeroCountClass = facilityCount === 0 ? ' is-zero-count' : ''
    return `
      <li class="${this.classNameModal()}::city-group-header${zeroCountClass}">
        <label class="${this.classNameModal()}::check">
          <input class="check-input" type="checkbox" data-group-type="prefecture-all" ${checked ? "checked='checked'" : ''}
            data-action="change->site--parts--retry-search--desktop--component#changeFormValue click->site--parts--retry-search--desktop--component#clickAreaCheckBox">
          <i></i>
          <span class="${this.classNameModal()}::checkbox-text">${prefectureName}内すべて${countText}</span>
        </label>
      </li>`
  }

  formatCount (n) {
    return n != null ? n.toLocaleString('ja-JP') : ''
  }

  renderOtherCitiesHeader () {
    return `
      <li class="${this.classNameModal()}::city-group-header">
        <span>その他の市区町村</span>
      </li>`
  }

  getPrefectureNameById (prefectureId) {
    if (!this.regionPrefectures || !prefectureId) return ''
    for (const prefs of Object.values(this.regionPrefectures)) {
      const pref = prefs.find(p => p.id === prefectureId)
      if (pref) return pref.name
    }
    return ''
  }

  syncChildCheckboxes (headerCheckbox) {
    const headerLi = headerCheckbox.closest('li')
    if (!headerLi) return
    const children = this.getGroupChildren(headerLi)
    children.forEach(child => {
      if (child.checked !== headerCheckbox.checked) {
        child.checked = headerCheckbox.checked
        const changeEvent = new Event('change', { bubbles: true })
        child.dispatchEvent(changeEvent)
      }
    })
  }

  syncParentCheckbox (childCheckbox) {
    const childLi = childCheckbox.closest('li')
    if (!childLi) return
    let sibling = childLi.previousElementSibling
    let headerLi = null
    while (sibling) {
      if (sibling.className.includes('city-group-header')) {
        headerLi = sibling
        break
      }
      sibling = sibling.previousElementSibling
    }
    if (!headerLi) return
    const headerCheckbox = headerLi.querySelector('input[type="checkbox"]')
    if (!headerCheckbox) return
    const children = this.getGroupChildren(headerLi)
    headerCheckbox.checked = children.length > 0 && children.every(c => c.checked)
  }

  getGroupChildren (headerLi) {
    const children = []
    let sibling = headerLi.nextElementSibling
    while (sibling) {
      if (sibling.className.includes('city-group-header')) break
      const checkbox = sibling.querySelector('input[type="checkbox"]')
      if (checkbox) children.push(checkbox)
      sibling = sibling.nextElementSibling
    }
    return children
  }

  // 汎用 LI 生成
  renderCityCheckboxLi ({ id, name, checked, inputName, targetName, facilityCount }) {
    const countText = facilityCount != null ? `<span class="${this.classNameModal()}::facility-count">(${this.formatCount(facilityCount)}件)</span>` : ''
    const zeroCountClass = facilityCount === 0 ? ' is-zero-count' : ''
    return `
      <li class="${this.classNameModal()}::checks${zeroCountClass}">
        <label class="${this.classNameModal()}::check">
          <input class="check-input" type="checkbox" value="${id}" ${checked ? "checked='checked'" : ''} name="${inputName}"
            data-action="change->site--parts--retry-search--desktop--component#changeFormValue click->site--parts--retry-search--desktop--component#clickAreaCheckBox"
            data-site--parts--retry-search--desktop--component-target="${targetName}">
          <i></i>
          <span class="${this.classNameModal()}::checkbox-text">${name}${countText}</span>
        </label>
      </li>`
  }

  shouldCheck (idList, inputTagValue) {
    return idList.includes(inputTagValue)
  }

  checkAll (e) {
    const currentIndex = this.checkboxAllTargets.indexOf(e.currentTarget)
    const checkboxes = this.checkboxesTargets[currentIndex].querySelectorAll('input[type="checkbox"]')
    const allCheckbox = e.currentTarget
    const isChecked = allCheckbox.checked

    checkboxes.forEach((checkbox) => {
      if (checkbox instanceof HTMLInputElement && checkbox.type === 'checkbox') {
        if (checkbox.checked !== isChecked) {
          checkbox.checked = isChecked
          const changeEvent = new Event('change', { bubbles: true })
          checkbox.dispatchEvent(changeEvent)
        }
      }
    })

    this.setFacilitiesCount()
  }

  // MEMO: input hiddenとinput checkboxどちらのパラメータが送られてしまうのでチェックボックスが生成されたらhidden要素を削除する
  removeInputHidden (idList, inputTagValue) {
    const inputHidden = idList.filter((element, _index) => { return parseInt(element.value) === inputTagValue })[0]
    if (typeof inputHidden !== 'undefined') { inputHidden.remove() }
  }

  async ensureCTAWithRetry (maxRetries = 3) {
    let attempt = 0
    const tryScroll = async () => {
      const ok = await this.scrollCTAIntoView()
      if (!ok && attempt < maxRetries - 1) {
        attempt += 1
        setTimeout(tryScroll, 50)
      }
    }
    tryScroll()
  }

  async scrollCTAIntoView () {
    const ctaButton = this.submitButtonTarget
    const scrollContainer = document.getElementById('panel-container')
    if (!ctaButton || !scrollContainer) return false

    const containerRect = scrollContainer.getBoundingClientRect()
    const buttonRect = ctaButton.getBoundingClientRect()
    const safe = 20

    const visible =
      buttonRect.bottom <= containerRect.bottom - safe &&
      buttonRect.top >= containerRect.top + safe

    if (visible) return true

    ctaButton.scrollIntoView({
      behavior: 'auto',
      block: 'end',
      inline: 'nearest',
    })

    await new Promise(resolve => setTimeout(resolve, 50))

    // 再検証
    const nb = ctaButton.getBoundingClientRect()
    const nc = scrollContainer.getBoundingClientRect()
    return (
      nb.bottom <= nc.bottom - safe &&
      nb.top >= nc.top + safe
    )
  }
}
